/**
 * Hash routing.
 *
 * The URL shape is fixed, not a choice: `#brief`, `#wallet`, `#meta/...` are
 * already in e-mails, in Stripe `success_url`s and in Whop `redirect_url`s that
 * have been issued. §7 of the migration plan puts them off limits.
 *
 * Since the four-section rework those old names are ALIASES rather than tabs:
 * nine screens became four sections plus one primary action, and every hash
 * that was ever handed out still has to land somewhere sensible. The alias
 * table below is that contract - deleting a row breaks a link that is already
 * in somebody's inbox.
 *
 * Two shapes carry meaning beyond the tab name, both preserved from the live
 * page's routeToHash():
 *
 *   #meta/<anything>     a Meta OAuth callback. Lands on settings, where the
 *                        Meta connection now lives.
 *   #...payment=success  the return from Stripe Checkout, which belongs to
 *                        onboarding step 5, not to a section at all.
 *
 * @module web/routes
 */

/** The four sections the sidebar offers. */
export const TABS = ["start", "work", "billing", "settings"] as const;

export type Tab = (typeof TABS)[number];

export const DEFAULT_TAB: Tab = "start";

/**
 * Where each section opens. `work` and `billing` stack several former screens,
 * and a deep link should land on the one it named rather than the first one.
 */
export const VIEWS = {
  work: ["list", "board"],
  billing: ["summary", "wallet", "membership"],
} as const;

export type WorkView = (typeof VIEWS.work)[number];
export type BillingView = (typeof VIEWS.billing)[number];

/**
 * Every hash ever handed out, mapped to where it lives now.
 *
 * The `view` half is what keeps an old link honest: `#wallet` used to be a
 * screen of its own, so it opens the billing section ON the wallet view, not
 * on the section's default. Same reasoning for `#board`.
 */
const ALIASES = {
  // former standalone screens
  overview: { tab: "start" },
  brief: { tab: "start" },
  projects: { tab: "work", view: "list" },
  board: { tab: "work", view: "board" },
  wallet: { tab: "billing", view: "wallet" },
  subscription: { tab: "billing", view: "membership" },
  meta: { tab: "settings" },
  // the sections themselves, so `#billing` keeps meaning what it always meant
  start: { tab: "start" },
  work: { tab: "work" },
  billing: { tab: "billing", view: "summary" },
  settings: { tab: "settings" },
} satisfies Record<string, { tab: Tab; view?: string }>;

/**
 * Every hash that is safe to link to.
 *
 * Wider than `Tab` on purpose: a link may point at `#wallet` or `#board`, which
 * are aliases rather than sections. Typing link targets against this instead of
 * `Tab` is what lets an old, still-valid destination survive the rework without
 * anybody hand-writing a raw string.
 */
export type Hash = keyof typeof ALIASES;

function isHash(value: string | undefined): value is Hash {
  return value !== undefined && Object.prototype.hasOwnProperty.call(ALIASES, value);
}

/**
 * What a hash means.
 *
 * Two deep-link shapes carry an id (spec 2026-09-20 work-cards):
 *   #work/board/<trelloCardId>   open the board WITH that card's panel open
 *   #start/<submissionId>        open Start with that submission's price block
 */
export type Route =
  | { kind: "tab"; tab: Tab; view?: string; card?: string; submission?: string }
  /** Back from Stripe Checkout: resume onboarding at the payment step. */
  | { kind: "payment-success" };

/**
 * Parse a location hash into a route.
 *
 * Anything unrecognised falls back to the default section rather than erroring:
 * a stale link from an old e-mail should land somewhere sensible, not on a
 * blank screen.
 */
export function parseHash(hash: string): Route {
  const h = hash.replace(/^#/, "") || DEFAULT_TAB;

  // Checked before the lookup: the live page checks it first too, and the
  // string carries a query fragment rather than a bare name.
  if (h.includes("payment=success")) return { kind: "payment-success" };

  // `#meta/<token>` is the OAuth callback; the tail is not ours to interpret.
  if (h.startsWith("meta/")) return { kind: "tab", tab: "settings" };

  // `#work/board` and friends: a section plus an explicit view, and optionally
  // an id after that: `#work/board/<cardId>`, `#start/<submissionId>`.
  const [head, tail, third] = h.split("/", 3);
  const hit = isHash(head) ? (ALIASES[head] as { tab: Tab; view?: string }) : undefined;
  if (!hit) return { kind: "tab", tab: DEFAULT_TAB };

  // Start has no views; its tail IS the submission id (a Work card that is
  // still to be confirmed points here).
  if (hit.tab === "start") {
    return tail ? { kind: "tab", tab: "start", submission: tail } : { kind: "tab", tab: "start" };
  }

  const view = tail ?? hit.view;
  if (view === undefined) return { kind: "tab", tab: hit.tab };
  // Only the board takes a card id; any other third segment is ignored rather
  // than breaking the link.
  if (hit.tab === "work" && view === "board" && third) {
    return { kind: "tab", tab: hit.tab, view, card: third };
  }
  return { kind: "tab", tab: hit.tab, view };
}
